import { Redirect, type UnknownOutputParams } from 'expo-router'
import type { ReactNode } from 'react'

import type { TEventOriginParam } from '@/src/general/constants/routes'
import { ErrorScreen } from '@/src/general/ui/screens/ErrorScreen'
import { useRoutes } from '@/src/general/utils/routes'

import type { TGetCommunityApiData } from '../../../api/getCommunity.types'
import { useCommunityQuery } from '../../../services/useCommunityQuery'
import { useCommunitySubscriptionsQuery } from '../../../services/useCommunitySubscriptionsQuery'
import { isVirtusCommunity } from '../../../utils/isVirtusCommunity'
import { VirtusCommunityScreen } from '../VirtusCommunityScreen'
import { CommunityPendingScreen } from './CommunityPendingScreen'
import { DefaultCommunityScreen } from './DefaultCommunityScreen'
import { useCommunityRedirections } from './useCommunityRedirections'
import { useIncrementCommunityViews } from './useIncrementCommunityViews'

type TCommunityScreenProps = TEventOriginParam & {
  id: number
  action?: string
  slug?: string
  searchParams: UnknownOutputParams
}

type TSubscription = ReturnType<typeof useCommunitySubscriptionsQuery>['activeSubscription']

type TCommunityVariant =
  | { kind: 'pending' }
  | { kind: 'virtus'; community: TGetCommunityApiData; subscription: NonNullable<TSubscription> }
  | { kind: 'default'; community: TGetCommunityApiData; subscription: TSubscription }

const getCommunityVariant = (
  community: TGetCommunityApiData | undefined,
  {
    activeSubscription: subscription,
    isLoading: isSubscriptionLoading,
  }: ReturnType<typeof useCommunitySubscriptionsQuery>,
): TCommunityVariant => {
  if (!community) return { kind: 'pending' }
  if (!isVirtusCommunity(community)) return { kind: 'default', community, subscription }
  if (isSubscriptionLoading) return { kind: 'pending' }
  if (subscription) return { kind: 'virtus', community, subscription }
  return { kind: 'default', community, subscription }
}

export const CommunityScreen = ({ id, action, origin, slug, searchParams }: TCommunityScreenProps): ReactNode => {
  const { routes } = useRoutes()
  useIncrementCommunityViews(id)

  const communityQuery = useCommunityQuery(id)
  const community = communityQuery.data
  const subscriptionsQuery = useCommunitySubscriptionsQuery(id, community?.lifetime)

  const { redirectPath } = useCommunityRedirections({
    community,
    forbidden: communityQuery.error?.name === 'Forbidden',
    id,
    origin,
    slug,
  })

  if (redirectPath) return <Redirect href={redirectPath} />

  if (communityQuery.isError)
    return (
      <ErrorScreen
        error={communityQuery.error}
        defaultErrorKey="community.load.error"
        fallbackRoute={routes.communities()}
      />
    )

  const variant = getCommunityVariant(community, subscriptionsQuery)
  const common = { action, searchParams }

  switch (variant.kind) {
    case 'pending':
      return <CommunityPendingScreen id={id} origin={origin} {...common} />
    case 'virtus':
      return <VirtusCommunityScreen {...variant} {...common} />
    case 'default':
      return <DefaultCommunityScreen {...variant} origin={origin} {...common} />
  }
}
